"use client";

import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import AuthButton from "@/app/(public)/(auth)/AuthButton";
import { useRouter } from "next/navigation";
import { signUp } from "@/core/auth/action.auth";
import authSettings from "@/shared/config/authSettings";
import { userCredentials } from "./validation";
import { Box, Field, Stack, Input, Text } from "@chakra-ui/react";
import {
  buildAuthRouteHref,
  isMcpConsentAuthorizationTarget,
  normalizeSafeInternalTarget,
} from "./authRedirectUtils";
import { authPageContent } from "./content";

type SignUpFormData = z.infer<typeof userCredentials>;

type SignUpFormProps = {
  targetUrl?: string;
};

export default function SignUpForm({ targetUrl }: SignUpFormProps) {
  // const initializeAccountPath = "/initialize-account";
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();
  const safeTargetUrl = normalizeSafeInternalTarget(targetUrl);
  const onboardingTargetUrl =
    safeTargetUrl ?? authSettings.redirects.privateHome;

  // Initialize React Hook Form
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignUpFormData>({
    resolver: zodResolver(userCredentials),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (data: SignUpFormData) => {
    setError(null);

    // Create FormData to maintain compatibility with server action
    const formData = new FormData();
    formData.append("email", data.email);
    formData.append("password", data.password);

    // Call server action
    const result = await signUp(formData, authSettings.autoLogin);

    // Handle the result
    if (result.status === "success") {
      if (authSettings.autoLogin) {
        if (
          !authSettings.allowFreshSignupMcpRegisterRedirect &&
          (safeTargetUrl === authSettings.redirects.oauthConsent ||
            isMcpConsentAuthorizationTarget(safeTargetUrl))
        ) {
          router.push(
            // buildAuthRouteHref(initializeAccountPath, safeTargetUrl),
            buildAuthRouteHref(
              authSettings.redirects.initializeAccount,
              safeTargetUrl,
            ),
          );
          return;
        }

        if (isMcpConsentAuthorizationTarget(safeTargetUrl)) {
          router.replace(safeTargetUrl);
          return;
        }

        router.push(
          // buildAuthRouteHref(initializeAccountPath, onboardingTargetUrl),
          buildAuthRouteHref(
            authSettings.redirects.initializeAccount,
            onboardingTargetUrl,
          ),
        );
      } else {
        router.push(
          buildAuthRouteHref(authSettings.redirects.login, safeTargetUrl),
        );
      }
    } else {
      setError(result.status);
    }
  };

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap="4">
          <Field.Root invalid={!!errors.email}>
            <Field.Label>Email</Field.Label>
            <Input
              {...register("email")}
              id="email"
              type="email"
              placeholder="Email"
              aria-invalid={!!errors.email}
            />
            {errors.email && (
              <Field.ErrorText color="moveon.danger">
                {errors.email.message}
              </Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root invalid={!!errors.password}>
            <Field.Label>Password</Field.Label>
            <Input
              {...register("password")}
              id="password"
              type="password"
              placeholder="Password"
              aria-invalid={!!errors.password}
            />
            {errors.password && (
              <Field.ErrorText color="moveon.danger">
                {errors.password.message}
              </Field.ErrorText>
            )}
          </Field.Root>

          <AuthButton
            type={authPageContent.forms.register.submitLabel}
            loading={isSubmitting}
          />

          {error && (
            <Text mt="1" fontSize="sm" color="moveon.danger" lineHeight="tall">
              {error}
            </Text>
          )}
        </Stack>
      </form>
    </Box>
  );
}
